import { useState } from "preact/hooks";
import { relativeTime, size } from "./format";
import { Icon, type IconName } from "./icons";
import { type OutboxEntry, cancel, describeAction, editMail, flushOutbox, useOutbox } from "./outbox";
import type { ComposeMode } from "./route";

interface Props {
  online: boolean;
  onMenu: () => void;
  /** A message taken out of the outbox to write again: it is a draft on the device now. */
  onEdit: (to: { mode: ComposeMode; source: string | null; draft: string }) => void;
  onError: (error: unknown) => void;
}

/** Mail written and actions taken without a connection, oldest first, until they are sent: how each stands, and a
 * way to take it out (mail also to edit, which a message the server refused needs). */
export function Outbox({ online, onMenu, onEdit, onError }: Props) {
  const { entries, sending } = useOutbox();
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = (e: OutboxEntry) => {
    if (e.kind === "mail" && !confirm("Delete this message? It has not been sent.")) return;
    run(() => cancel(e.seq!));
  };

  const edit = (e: OutboxEntry) =>
    run(async () => {
      const to = await editMail(e.seq!);
      if (to) onEdit(to);
    });

  return (
    <div class="offline-settings">
      <div class="toolbar">
        <button class="icon-button menu" title="Folders" onClick={onMenu}>
          <Icon name="menu" />
        </button>
        <h2 class="title">Outbox</h2>
        <span class="spacer" />
        {entries.length > 0 && (
          <button class="text-button" disabled={!online || busy || sending !== null} onClick={() => run(() => flushOutbox(true))}>
            Send now
          </button>
        )}
      </div>
      <div class="settings-body">
        <p class="muted">
          Mail written and actions taken without a connection wait here and are sent in the order they were made as soon as the app can reach the server. They are
          not sent in the background: only while the app is open.
        </p>
        {!online && entries.length > 0 && <p class="warn-text">No connection: waiting for one.</p>}
        {entries.length === 0 ? (
          <p class="muted small">Nothing is waiting to be sent.</p>
        ) : (
          <ul class="device-list outbox-list" role="list">
            {entries.map((e) => {
              const now = sending === e.seq;
              return (
                <li key={e.seq}>
                  <Icon name={icon(e)} size={18} />
                  <div class="outbox-entry">
                    {e.kind === "mail" ? (
                      <>
                        <div class="outbox-subject">{e.subject || "(no subject)"}</div>
                        <div class="muted small">
                          To {e.request.to.join(", ")}
                          {e.files.length + (e.request.attachments?.length ?? 0) > 0 && ` · ${attachments(e)}`}
                        </div>
                      </>
                    ) : (
                      <div>{describeAction(e)}</div>
                    )}
                    <div class={`small ${e.refused ? "warn-text" : "muted"}`}>{state(e, now)}</div>
                  </div>
                  <div class="outbox-actions">
                    {e.kind === "mail" && (
                      <button class="text-button" disabled={busy || now} onClick={() => edit(e)}>
                        Edit
                      </button>
                    )}
                    <button class="text-button" disabled={busy || now} onClick={() => remove(e)}>
                      {e.kind === "mail" ? "Delete" : "Cancel"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function icon(e: OutboxEntry): IconName {
  if (e.kind === "mail") return "sent";
  if (e.kind === "read" || e.kind === "read-many") return e.read ? "read" : "unread";
  if (e.kind === "flag" || e.kind === "flag-many") return "star";
  return e.kind === "trash" || e.kind === "trash-many" ? "trash" : "move";
}

function attachments(e: OutboxEntry & { kind: "mail" }): string {
  const n = e.files.length + (e.request.attachments?.length ?? 0);
  const bytes = e.files.reduce((sum, f) => sum + f.size, 0);
  return `${n} ${n === 1 ? "attachment" : "attachments"}${bytes > 0 ? ` (${size(bytes)} from this device)` : ""}`;
}

function state(e: OutboxEntry, now: boolean): string {
  if (now) return "Sending…";
  const made = `${e.kind === "mail" ? "Written" : "Made"} ${relativeTime(e.created_at)}`;
  if (e.refused) return `Not sent: ${e.error}. Edit it, or delete it.`;
  if (e.error) return `${made} · failed (${e.error}); trying again`;
  return `${made} · waiting`;
}
